import { ReactNode, createContext, useCallback, useContext, useMemo } from 'react';

import { GetCheckoutSummaryQuery as GetCheckoutSummary } from '__generated__/graphql';

import NetworkError from '../../common/NetworkError';
import StateContext from './StateContext';
import useBaseCheckoutSummary from '../../common/useCheckoutSummary';

type CheckoutSummary = GetCheckoutSummary['checkoutSummary'];

interface CheckoutSummaryContextValue {
  checkoutSummary?: CheckoutSummary;
  refetch?: () => unknown;
  loading?: boolean;
}

export const CheckoutSummaryContext = createContext({} as CheckoutSummaryContextValue);

export const useCheckoutSummary = () => useContext(CheckoutSummaryContext);

interface CheckoutSummaryProviderProps {
  children?: ReactNode;
}

const CheckoutSummaryProvider = ({ children }: CheckoutSummaryProviderProps) => {
  const { form, session, setQueueToken } = useContext(StateContext);

  const onChange = useCallback((data: GetCheckoutSummary) => {
    const { queue } = data.checkoutSummary;

    if (queue) {
      // Reset the token so that the queue knows it should start polling, in case it was activated before.
      setQueueToken(null);
    }
  }, [setQueueToken]);

  const { checkoutSummary, refetch, loading, error } = useBaseCheckoutSummary({
    event: {
      id: form.event.id,
    },
    cart: form.participant?.email ? { email: form.participant.email } : null,
    registrations: form.registrations,
    invitationCode: form.invitation_code,
    coupon: form.coupon,
    vatId: session.invoiceDetails || form.registrations.business.length > 0 ? form.invoice?.vat_id : undefined,
    queueToken: session.queueToken,
    applyDiscounts: true,
    onChange,
  });

  const value = useMemo(() => ({
    checkoutSummary,
    refetch,
    loading,
  }), [checkoutSummary, refetch, loading]);

  if (error) {
    // The error is only displayed if after a while of retrying, the query still fails.
    return <NetworkError />;
  }

  return (
    <CheckoutSummaryContext.Provider value={value}>
      {children}
    </CheckoutSummaryContext.Provider>
  );
};

export default CheckoutSummaryProvider;
